import { useState, type FormEvent, type ReactNode } from 'react'
import { Notice } from '../Toast'
import type { Answers, Details, FieldDef, Guardian } from '../api/types'
import { isMinor } from '../lib/age'
import { visibleFields } from '../lib/answers'
import DynamicFields from './DynamicFields'

interface Props {
  fields: FieldDef[]
  initial: { name: string; mobile: string; dateOfBirth: string; values: Answers; guardian: Guardian | null }
  /** Problems from the server, keyed by field (name, mobile, dateOfBirth, guardian.name, or a question's key). */
  errors: Record<string, string>
  busy: boolean
  submitLabel: string
  /** Rendered between the basics and the campaign's questions, e.g. the email and program on the registration page. */
  extra?: ReactNode
  onSubmit: (d: Details) => void
  idPrefix?: string
  /** False when the campaign lets under-18s consent for themselves: no guardian details are asked. */
  guardianRequired?: boolean
}

/** The basics every candidate gives, the parent or guardian for under-18s, and the campaign's own questions. */
export default function DetailsForm({ fields, initial, errors, busy, submitLabel, extra, onSubmit, idPrefix = 'd', guardianRequired = true }: Props) {
  const [name, setName] = useState(initial.name)
  const [mobile, setMobile] = useState(initial.mobile)
  const [dob, setDob] = useState(initial.dateOfBirth)
  const [values, setValues] = useState<Answers>(initial.values)
  const [guardian, setGuardian] = useState<Guardian>(initial.guardian ?? { name: '', email: '', mobile: '' })
  const minor = isMinor(dob)

  const submit = (e: FormEvent) => {
    e.preventDefault()
    onSubmit({ name, mobile, dateOfBirth: dob, values, guardian: minor && guardianRequired ? guardian : null })
  }
  const err = (k: string) => (errors[k] ? <p id={`${idPrefix}-${k}-err`} role="alert" className="field-error">{errors[k]}</p> : null)
  const bad = (k: string) => (errors[k] ? true : undefined)
  const problems = Object.keys(errors).length

  return (
    <form onSubmit={submit} noValidate>
      {problems > 0 && (
        <Notice tone="error">
          {problems === 1 ? 'One thing needs fixing' : `${problems} things need fixing`}: check the highlighted questions below.
        </Notice>
      )}
      <section className="card">
        <label className="field">Full name
          <input id={`${idPrefix}-name`} type="text" autoComplete="name" value={name} aria-invalid={bad('name')} aria-describedby={errors.name ? `${idPrefix}-name-err` : undefined} onChange={(e) => setName(e.target.value)} />
        </label>
        {err('name')}
        {extra}
        <div className="row two">
          <div>
            <label className="field">Mobile number
              <input id={`${idPrefix}-mobile`} type="tel" inputMode="tel" autoComplete="tel" value={mobile} aria-invalid={bad('mobile')}
                aria-describedby={[`${idPrefix}-mobile-hint`, errors.mobile ? `${idPrefix}-mobile-err` : null].filter(Boolean).join(' ')} onChange={(e) => setMobile(e.target.value)} />
            </label>
            <p id={`${idPrefix}-mobile-hint`} className="hint">10 digits, with country code if you like</p>
            {err('mobile')}
          </div>
          <div>
            <label className="field">Date of birth
              <input id={`${idPrefix}-dob`} type="date" autoComplete="bday" value={dob} aria-invalid={bad('dateOfBirth')} aria-describedby={errors.dateOfBirth ? `${idPrefix}-dateOfBirth-err` : undefined} onChange={(e) => setDob(e.target.value)} />
            </label>
            {err('dateOfBirth')}
          </div>
        </div>
      </section>

      {minor && guardianRequired && (
        <section className="card" aria-live="polite">
          <h2>Parent or guardian</h2>
          <p className="muted">You are under 18, so a parent or guardian must give consent before you can take the tests. We will email them a link.</p>
          <label className="field">Their full name
            <input type="text" value={guardian.name} aria-invalid={bad('guardian.name')} onChange={(e) => setGuardian({ ...guardian, name: e.target.value })} />
          </label>
          {err('guardian.name')}
          <div className="row two">
            <div>
              <label className="field">Their email
                <input type="email" value={guardian.email} aria-invalid={bad('guardian.email')} onChange={(e) => setGuardian({ ...guardian, email: e.target.value })} />
              </label>
              {err('guardian.email')}
            </div>
            <div>
              <label className="field">Their mobile (optional)
                <input type="tel" inputMode="tel" value={guardian.mobile ?? ''} aria-invalid={bad('guardian.mobile')} onChange={(e) => setGuardian({ ...guardian, mobile: e.target.value })} />
              </label>
              {err('guardian.mobile')}
            </div>
          </div>
        </section>
      )}

      <DynamicFields fields={visibleFields(fields, minor)} values={values} errors={errors} idPrefix={idPrefix}
        onChange={(k, v) => setValues((prev) => ({ ...prev, [k]: v }))} />

      <button className="primary" type="submit" disabled={busy}>{busy ? 'Saving…' : submitLabel}</button>
    </form>
  )
}
